// Pengaturan Inventory — halaman BARU. Backend-nya sudah lama siap
// (routers/inventory/purchase.py: GET/PATCH /inventory/pengaturan/*) tapi
// tidak pernah punya layar, sehingga satu-satunya cara mengubahnya adalah
// lewat database langsung — sama saja tidak ada fiturnya untuk owner.
//
// Tiga (+1) bagian, semuanya digerbangi izin `inventory_po` yang SUDAH ADA
// (bukan modul baru): "view" untuk membuka halaman, "update" untuk mengubah.
// Tombol Simpan pada "Ambang Approval PO" TAMBAHAN digerbangi role approver
// (SPV/CEO Finance/admin/superadmin) — backend menolak selain itu (lihat
// `require_approver` di routers/inventory/purchase.py), jadi tombolnya
// disembunyikan supaya orang yang izin update-nya ada tapi rolenya bukan
// approver tidak menekan tombol yang ujungnya cuma 403.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import InputRupiah from "../components/InputRupiah";
import {
  Settings2, Hash, AlertTriangle, ShieldCheck, Bell, Save, ShieldAlert,
  ArrowLeft, Info, CheckCircle2, XCircle,
} from "lucide-react";

const PREFIX_LIST = ["PO", "DO", "TRM"];
const PREFIX_INFO = {
  PO: { label: "Purchase Order (PO)", ket: "Dipakai saat membuat pesanan pembelian ke supplier." },
  DO: { label: "Penerimaan Barang (DO)", ket: "Dipakai saat barang dari PO diterima di gudang." },
  TRM: { label: "Penerimaan Non-Pembelian (TRM)", ket: "Dipakai untuk barang masuk BUKAN dari pembelian — hibah, sitaan, hadiah, inventaris." },
};

const RESET_OPTIONS = [
  { v: "tahun", l: "Setiap tahun — nomor urut kembali ke 1 tiap tanggal 1 Januari" },
  { v: "bulan", l: "Setiap bulan — nomor urut kembali ke 1 tiap awal bulan" },
  { v: "hari", l: "Setiap hari — nomor urut kembali ke 1 tiap hari (cocok untuk dokumen bervolume harian)" },
  { v: "tidak", l: "Tidak pernah — nomor urut terus naik, tidak pernah kembali ke 1" },
];

const TOKEN_INFO = [
  { t: "{prefix}", ket: "Kode dokumennya sendiri (PO, DO, atau TRM)" },
  { t: "{tahun}", ket: "Tahun saat dokumen dibuat, 4 angka (contoh: 2026)" },
  { t: "{bulan}", ket: "Bulan saat dokumen dibuat, selalu 2 angka (contoh: 08 untuk Agustus)" },
  { t: "{hari}", ket: "Tanggal (hari dalam bulan) saat dokumen dibuat, selalu 2 angka (contoh: 05)" },
  { t: "{urut}", ket: "Nomor urut otomatis yang naik sendiri tiap dokumen baru dibuat" },
];

// Jeda debounce sebelum memanggil pratinjau ke backend tiap owner mengetik —
// dulu pratinjau dirangkai di sisi klien (SELALU "00001", tidak pernah
// membaca counter sungguhan, dan tidak kenal token {hari} — brief owner 17
// Agu #11). Sekarang backend yang menghitung (`GET .../pratinjau`, baca
// `doc_number_counters` TANPA mengonsumsinya), jadi tiap ketikan perlu
// panggilan API — didebounce supaya tidak membanjiri backend per huruf.
const DEBOUNCE_PRATINJAU_MS = 400;

const inpCls = "h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:bg-slate-50 disabled:text-slate-400";

function Kartu({ icon: Icon, warna = "emerald", judul, sub, children, testId }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg bg-${warna}-50 text-${warna}-600 grid place-items-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-display font-bold text-base text-slate-900">{judul}</h2>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function InventoryPengaturan() {
  const { user, can } = useAuth();
  const canView = can("inventory_po", "view");
  const canUpdate = can("inventory_po", "update");
  // require_approver di backend (routers/inventory/purchase.py::atur_ambang_po_approval)
  // hanya meloloskan role ini — walau canUpdate true, role lain akan 403 saat submit.
  const bolehAturApproval = ["finance_spv", "finance_ceo", "admin", "superadmin"].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [nomor, setNomor] = useState({});
  // prefix -> { nomor, urut_berikutnya, error, loading } — dibaca dari
  // backend (`GET .../pratinjau`), BUKAN dikarang di sini lagi.
  const [preview, setPreview] = useState({});
  const [busyPrefix, setBusyPrefix] = useState("");
  const [hargaWajar, setHargaWajar] = useState(20);
  const [busyHarga, setBusyHarga] = useState(false);
  const [poApproval, setPoApproval] = useState(0);
  const [busyApproval, setBusyApproval] = useState(false);
  const [stokKritis, setStokKritis] = useState({ enabled: true, ringkas_maks_item: 15 });
  const [busyStokKritis, setBusyStokKritis] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [n, h, p, s] = await Promise.all([
        api.get("/inventory/pengaturan/nomor-dokumen"),
        api.get("/inventory/pengaturan/harga-wajar"),
        api.get("/inventory/pengaturan/po-approval"),
        api.get("/inventory/pengaturan/stok-kritis-alert"),
      ]);
      setNomor(n.data.items || {});
      setHargaWajar(h.data.ambang_persen ?? 20);
      setPoApproval(p.data.ambang_nilai ?? 0);
      setStokKritis(s.data || { enabled: true, ringkas_maks_item: 15 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat pengaturan Inventory");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Pratinjau nomor dokumen berikutnya (baca counter sungguhan) ----------
  // Backend, BUKAN dikarang di sini: `format`/`reset`/`digit` yang SEDANG
  // diketik owner (belum tentu sudah disimpan) dikirim sebagai override query
  // param supaya pratinjau tetap "hidup saat mengetik" seperti versi lama,
  // tapi nomor URUT-nya selalu dari `doc_number_counters` yang sebenarnya —
  // lihat `pratinjau_nomor_berikutnya` di routers/inventory/_shared.py.
  useEffect(() => {
    if (!canView || loading) return;
    const timer = setTimeout(() => {
      PREFIX_LIST.forEach((prefix) => {
        const cfg = nomor[prefix];
        if (!cfg) return;
        api.get(`/inventory/pengaturan/nomor-dokumen/${prefix}/pratinjau`, {
          params: { format: cfg.format, reset: cfg.reset, digit: Number(cfg.digit) || 5 },
        }).then((r) => {
          setPreview((s) => ({ ...s, [prefix]: { nomor: r.data.nomor, error: null } }));
        }).catch((e) => {
          setPreview((s) => ({
            ...s,
            [prefix]: { nomor: null, error: e?.response?.data?.detail || "Gagal memuat pratinjau" },
          }));
        });
      });
    }, DEBOUNCE_PRATINJAU_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomor, canView, loading]);

  // ---------- Format nomor dokumen ----------
  const ubahNomor = (prefix, patch) => setNomor((s) => ({ ...s, [prefix]: { ...s[prefix], ...patch } }));

  const simpanNomor = async (prefix) => {
    const cfg = nomor[prefix] || {};
    const digit = Number(cfg.digit) || 5;
    if (digit < 1 || digit > 10) { toast.error("Jumlah digit nomor urut harus 1-10"); return; }
    setBusyPrefix(prefix);
    try {
      const r = await api.patch(`/inventory/pengaturan/nomor-dokumen/${prefix}`, {
        format: cfg.format, reset: cfg.reset, digit,
      });
      toast.success(r.data.message);
      setNomor((s) => ({ ...s, [prefix]: r.data.config }));
    } catch (e) { toast.error(e?.response?.data?.detail || `Gagal menyimpan format nomor ${prefix}`); }
    finally { setBusyPrefix(""); }
  };

  // ---------- Ambang harga wajar ----------
  const simpanHargaWajar = async () => {
    const nilai = Number(hargaWajar);
    if (!nilai || nilai <= 0) { toast.error("Ambang harus lebih dari 0 persen"); return; }
    setBusyHarga(true);
    try {
      const r = await api.patch("/inventory/pengaturan/harga-wajar", { ambang_persen: nilai });
      toast.success(r.data.message);
      setHargaWajar(r.data.ambang_persen);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan ambang harga wajar"); }
    finally { setBusyHarga(false); }
  };

  // ---------- Ambang approval PO ----------
  const simpanApproval = async () => {
    setBusyApproval(true);
    try {
      const r = await api.patch("/inventory/pengaturan/po-approval", { ambang_nilai: Number(poApproval) || 0 });
      toast.success(r.data.message);
      setPoApproval(r.data.ambang_nilai);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan ambang approval PO"); }
    finally { setBusyApproval(false); }
  };

  // ---------- Notifikasi stok kritis ----------
  const simpanStokKritis = async (patch) => {
    setBusyStokKritis(true);
    try {
      const r = await api.patch("/inventory/pengaturan/stok-kritis-alert", patch);
      toast.success(r.data.message);
      setStokKritis(r.data.config);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan notifikasi stok kritis"); }
    finally { setBusyStokKritis(false); }
  };

  if (!canView) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="inv-pengaturan-tidak-punya-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke Pengaturan Inventory</p>
          <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin modul "Inventory · PO &amp; Penerimaan".</p>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-8 text-sm text-slate-500" data-testid="inv-pengaturan-loading">Memuat pengaturan...</div>;

  return (
    <div className="p-4 md:p-8 max-w-4xl space-y-6" data-testid="inventory-pengaturan">
      <header>
        <Link to="/inventory/pembelian" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 mb-2" data-testid="inv-pengaturan-kembali">
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Pembelian &amp; Terima
        </Link>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1.5">
          <Settings2 className="w-3.5 h-3.5" /> Pengaturan
        </p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Pengaturan Inventory</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Format nomor dokumen, ambang peringatan harga, ambang approval PO, dan notifikasi stok kritis — semuanya
          bisa diatur di sini tanpa perlu minta bantuan developer.
        </p>
      </header>

      {!canUpdate && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="inv-pengaturan-readonly">
          Anda hanya bisa melihat pengaturan ini. Hubungi admin untuk melakukan perubahan.
        </div>
      )}

      {/* ============ 1. Format Nomor Dokumen ============ */}
      <Kartu icon={Hash} judul="Format Nomor Dokumen" testId="kartu-nomor-dokumen"
             sub="Atur pola penomoran otomatis untuk PO, DO, dan penerimaan non-pembelian — seperti Accurate Online.">
        <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
          <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Kode yang bisa dipakai di format:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {TOKEN_INFO.map((t) => (
              <p key={t.t} className="text-[11px] text-slate-600">
                <code className="px-1 py-0.5 rounded bg-white border border-slate-200 text-emerald-700 font-mono">{t.t}</code> — {t.ket}
              </p>
            ))}
          </div>
        </div>

        <div className="space-y-5">
          {PREFIX_LIST.map((prefix) => {
            const cfg = nomor[prefix] || { format: "{prefix}-{tahun}-{urut}", reset: "tahun", digit: 5 };
            const pv = preview[prefix];
            const error = pv?.error || null;
            const hasil = pv?.nomor;
            const belumSiap = !pv; // debounce belum selesai / permintaan pertama masih berjalan
            return (
              <div key={prefix} className="border border-slate-200 rounded-lg p-4" data-testid={`nomor-dok-row-${prefix}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{PREFIX_INFO[prefix].label}</p>
                    <p className="text-[11px] text-slate-500">{PREFIX_INFO[prefix].ket}</p>
                  </div>
                  {canUpdate && (
                    <button onClick={() => simpanNomor(prefix)} disabled={busyPrefix === prefix || !!error}
                            data-testid={`nomor-dok-simpan-${prefix}`}
                            className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                      <Save className="w-3.5 h-3.5" /> {busyPrefix === prefix ? "Menyimpan..." : "Simpan"}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1" htmlFor={`fmt-${prefix}`}>
                      Format
                    </label>
                    <input id={`fmt-${prefix}`} type="text" value={cfg.format || ""} disabled={!canUpdate}
                           onChange={(e) => ubahNomor(prefix, { format: e.target.value })}
                           data-testid={`nomor-dok-format-${prefix}`}
                           placeholder="{prefix}-{tahun}-{urut}"
                           className={`${inpCls} w-full font-mono`} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1" htmlFor={`digit-${prefix}`}>
                      Jumlah digit urut
                    </label>
                    <input id={`digit-${prefix}`} type="number" min={1} max={10} value={cfg.digit ?? 5} disabled={!canUpdate}
                           onChange={(e) => ubahNomor(prefix, { digit: e.target.value })}
                           data-testid={`nomor-dok-digit-${prefix}`}
                           className={`${inpCls} w-28 text-center`} />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Kapan nomor urut kembali ke 1?</label>
                  <div className="flex flex-col gap-1.5">
                    {RESET_OPTIONS.map((opt) => (
                      <label key={opt.v} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                        <input type="radio" name={`reset-${prefix}`} checked={(cfg.reset || "tahun") === opt.v}
                               disabled={!canUpdate} onChange={() => ubahNomor(prefix, { reset: opt.v })}
                               data-testid={`nomor-dok-reset-${prefix}-${opt.v}`}
                               className="w-3.5 h-3.5 accent-emerald-600" />
                        {opt.l}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Pratinjau BACA COUNTER SUNGGUHAN dari backend (brief owner 17 Agu
                    #11 — dulu dikarang di sisi klien, SELALU "00001"). Didebounce
                    tiap owner mengetik (lihat DEBOUNCE_PRATINJAU_MS), tapi TIDAK
                    PERNAH mengonsumsi nomor (backend baca counter, bukan $inc). */}
                <div className={`mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 ${error ? "bg-rose-50 border border-rose-200" : "bg-emerald-50 border border-emerald-200"}`}>
                  {error ? <XCircle className="w-4 h-4 text-rose-600 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Pratinjau nomor dokumen berikutnya</p>
                    {error ? (
                      <p className="text-sm font-mono font-bold text-rose-700" data-testid={`nomor-dok-preview-${prefix}`}>{error}</p>
                    ) : (
                      <p className="text-sm font-mono font-bold text-emerald-800" data-testid={`nomor-dok-preview-${prefix}`}>
                        {belumSiap ? "Memuat pratinjau…" : hasil}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Kartu>

      {/* ============ 2. Ambang Harga Wajar ============ */}
      <Kartu icon={AlertTriangle} warna="amber" judul="Ambang Peringatan Harga Wajar" testId="kartu-harga-wajar"
             sub="Berapa persen penyimpangan harga beli dari rata-rata histori supplier yang memicu peringatan.">
        <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800">
          <b>Ini hanya PERINGATAN, bukan penolakan.</b> Kalau harga beli di PO/penerimaan menyimpang dari rata-rata
          harga sebelumnya (untuk supplier &amp; barang yang sama) melebihi persentase ini, sistem menampilkan
          peringatan — tapi dokumennya tetap bisa disimpan. Harga memang wajar naik-turun (kurs, promo, dsb).
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1" htmlFor="harga-wajar-persen">
              Ambang penyimpangan (%)
            </label>
            <div className="relative">
              <input id="harga-wajar-persen" type="number" min={0.1} step={0.1} value={hargaWajar} disabled={!canUpdate}
                     onChange={(e) => setHargaWajar(e.target.value)}
                     data-testid="harga-wajar-input"
                     className={`${inpCls} w-32 pr-7`} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
            </div>
          </div>
          {canUpdate && (
            <button onClick={simpanHargaWajar} disabled={busyHarga} data-testid="harga-wajar-simpan"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {busyHarga ? "Menyimpan..." : "Simpan"}
            </button>
          )}
        </div>
      </Kartu>

      {/* ============ 3. Ambang Approval PO ============ */}
      <Kartu icon={ShieldCheck} warna="sky" judul="Ambang Approval PO" testId="kartu-po-approval"
             sub="Nilai PO minimal yang wajib disetujui SPV/CEO/admin sebelum barangnya bisa diterima.">
        <div className="mb-3 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2.5 text-xs text-sky-800">
          Isi <b>0</b> untuk mematikan fitur ini — semua PO baru langsung berstatus "terbuka" seperti biasa.
          Isi angka lebih dari 0: setiap PO bernilai <b>sama dengan atau lebih besar</b> dari angka ini akan berstatus
          <b> "menunggu approval"</b> dan tidak bisa menerima barang sampai disetujui SPV/CEO Finance/admin.
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1" htmlFor="po-approval-nilai">
              Ambang nilai PO
            </label>
            <InputRupiah value={poApproval} onChange={(n) => setPoApproval(n)} testid="po-approval-input"
                         disabled={!bolehAturApproval}
                         className={`${inpCls} w-48`} />
          </div>
          {bolehAturApproval ? (
            <button onClick={simpanApproval} disabled={busyApproval} data-testid="po-approval-simpan"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {busyApproval ? "Menyimpan..." : "Simpan"}
            </button>
          ) : canUpdate && (
            <p className="text-[11px] text-slate-500 max-w-xs">Hanya SPV/CEO Finance/admin yang bisa mengubah ambang ini.</p>
          )}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Status sekarang: {Number(poApproval) > 0
            ? <span className="text-sky-700 font-semibold">aktif — PO ≥ Rp {Number(poApproval).toLocaleString("id-ID")} butuh approval</span>
            : <span className="text-slate-500 font-semibold">nonaktif</span>}
        </p>
      </Kartu>

      {/* ============ 4. Notifikasi Stok Kritis ============ */}
      <Kartu icon={Bell} warna="rose" judul="Notifikasi Stok Kritis" testId="kartu-stok-kritis"
             sub="Atur pengiriman ringkasan harian barang yang stoknya sudah di bawah ambang minimum (Master Produk).">
        <div className="mb-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-600">
          Ambang minimum PER BARANG diatur lewat kolom "Min. Stok" di Master Produk — pengaturan di sini hanya
          mengatur PERILAKU PENGIRIMAN notifikasinya: dinyalakan/dimatikan, dan berapa banyak barang ditampilkan
          satu-satu sebelum sisanya diringkas "...dan N barang lainnya".
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={!!stokKritis.enabled} disabled={!canUpdate || busyStokKritis}
                   onChange={(e) => simpanStokKritis({ enabled: e.target.checked })}
                   data-testid="stok-kritis-enabled"
                   className="w-4 h-4 accent-emerald-600" />
            Aktifkan notifikasi harian stok kritis
          </label>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1" htmlFor="stok-kritis-maks">
              Maks. barang ditampilkan per pesan
            </label>
            <input id="stok-kritis-maks" type="number" min={1} max={200} value={stokKritis.ringkas_maks_item ?? 15}
                   disabled={!canUpdate || !stokKritis.enabled}
                   onChange={(e) => setStokKritis((s) => ({ ...s, ringkas_maks_item: e.target.value }))}
                   onBlur={(e) => {
                     const n = Number(e.target.value);
                     if (n >= 1) simpanStokKritis({ ringkas_maks_item: n });
                   }}
                   data-testid="stok-kritis-maks-item"
                   className={`${inpCls} w-24 text-center`} />
          </div>
        </div>
      </Kartu>

    </div>
  );
}
